// Reglas puras de permisos compartidas backend/frontend. Fuente única.
const { EXPEDIENT_TYPES } = require('../constants/expedient');

/**
 * ¿Puede el rol comunicarse con el deudor (WhatsApp/SMS/email/llamada) en un
 * expediente de este tipo?
 * - admin: siempre
 * - company / gestor: solo en AR (account-receivable)
 * - operator y demás: nunca
 * @param {string} role
 * @param {string} expedientType
 * @returns {boolean}
 */
function canCommunicate(role, expedientType) {
  if (role === 'admin') return true;
  if (role === 'company' || role === 'gestor') {
    return expedientType === EXPEDIENT_TYPES.ACCOUNT_RECEIVABLE;
  }
  return false;
}

/**
 * ¿Puede el rol ver/gestionar los assignments de un expediente de este tipo?
 * - admin: siempre
 * - gestor: solo en AR (fuera de AR es como un operator: ve y anota, sin
 *   assignments ni comunicación)
 * - resto (company/operator/client): nunca
 * @param {string} role
 * @param {string} expedientType
 * @returns {boolean}
 */
function canManageAssignments(role, expedientType) {
  if (role === 'admin') return true;
  // La empresa, igual que su gestor: es la dueña del equipo y de sus AR
  // (22/10/2026, docs/tareas-para-la-empresa-plan.md).
  if (role === 'gestor' || role === 'company') return expedientType === EXPEDIENT_TYPES.ACCOUNT_RECEIVABLE;
  return false;
}

/**
 * Interruptor del auto-servicio de conexión de WhatsApp.
 *
 * **Abierto desde el 04/09/2026**: el dueño de una empresa puede lanzar el alta
 * de SU propio número, sin esperar a que lo haga un admin. Estuvo cerrado
 * durante el piloto para acompañar el proceso, y ya no hace falta.
 *
 * Lo que sigue cerrado, y es lo que de verdad protegía: la opción `dedicated`
 * —la que apaga el número en el móvil del cliente— **sigue siendo solo de
 * admin**, se compruebe este interruptor o no. Abrir el auto-servicio deja
 * pedir el enlace; no deja tomar la decisión difícil de deshacer.
 *
 * Es una constante y NO una variable de entorno a propósito: este fichero lo
 * comparten backend y frontend, y en el navegador no hay `process.env`. Con una
 * variable, el backend leería una cosa y la pantalla otra — el botón aparecería
 * y el envío lo rechazaría.
 */
const WHATSAPP_SELF_SERVICE_ENABLED = true;

/**
 * ¿Puede este usuario lanzar la conexión del WhatsApp de una empresa?
 * - admin: siempre
 * - company: solo sobre SU propia empresa, y solo con el auto-servicio abierto
 * - resto (operator/gestor/client): nunca
 *
 * @param {string} role      rol del usuario conectado
 * @param {string} userId    id del usuario conectado
 * @param {string} companyId id de la empresa cuyo WhatsApp se quiere conectar
 * @returns {boolean}
 */
function canConnectWhatsapp(role, userId, companyId) {
  if (role === 'admin') return true;
  if (!WHATSAPP_SELF_SERVICE_ENABLED) return false;
  return role === 'company' && !!userId && String(userId) === String(companyId);
}

/**
 * ¿Puede el rol conectar y administrar el correo (dominios de envío) de una
 * company?
 * - admin: cualquiera
 * - company: **solo la suya**
 * - gestor / operator / client: nunca
 *
 * Es más restrictivo que `canCommunicate` a propósito: un gestor manda correos
 * en AR, pero conectar un dominio cambia **la identidad con la que la empresa
 * se dirige a sus deudores** y compromete la reputación de ese dominio. Eso lo
 * decide el dueño, no quien gestiona el día a día.
 *
 * @param {string} role
 * @param {string} ownCompanyId    company del usuario (null si no es company)
 * @param {string} targetCompanyId company sobre la que se quiere actuar
 * @returns {boolean}
 */
function canManageCompanyEmail(role, ownCompanyId, targetCompanyId) {
  if (role === 'admin') return true;
  if (role !== 'company') return false;
  if (!ownCompanyId || !targetCompanyId) return false;
  return String(ownCompanyId) === String(targetCompanyId);
}

module.exports = {
  canCommunicate,
  canManageAssignments,
  canConnectWhatsapp,
  WHATSAPP_SELF_SERVICE_ENABLED,
  canManageCompanyEmail,
};
